import React, { useEffect, useRef } from "react";

type HeroWaveProps = {
  className?: string;
};

const HeroWave: React.FC<HeroWaveProps> = ({ className }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Offscreen low-res buffer (putImageData is expensive; keep it small)
    const buffer = document.createElement("canvas");
    const bctx = buffer.getContext("2d", { willReadFrequently: true });
    if (!bctx) return;

    let w = 0;
    let h = 0;
    let imageData: ImageData | null = null;
    let data: Uint8ClampedArray | null = null;
    let rafId: number | null = null;

    // Adaptive quality controls
    const isCoarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
    // Slightly higher baseline quality; adaptive downshifts on slower devices.
    const initialTargetPixels = isCoarsePointer ? 320_000 : 520_000;
    let targetPixels = initialTargetPixels;
    const MIN_PIXELS = 220_000;
    const MAX_PIXELS = 900_000;
    const TARGET_FRAME_MS = 16.7; // ~60fps

    // Frame timing / adaptation state
    let lastT = performance.now();
    let emaFrame = TARGET_FRAME_MS;
    let adaptAccumMs = 0;
    let adaptCount = 0;

    const TAU = Math.PI * 2;

    const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

    const computeBufferSize = () => {
      const vw = Math.max(1, window.innerWidth);
      const vh = Math.max(1, window.innerHeight);

      // Use DPR for sharp scaling, but cap to avoid exploding work.
      const dpr = clamp(window.devicePixelRatio || 1, 1, 2);

      canvas.width = Math.floor(vw * dpr);
      canvas.height = Math.floor(vh * dpr);

      const aspect = canvas.width / canvas.height;
      // Derive low-res buffer size from target pixel budget.
      let lowH = Math.sqrt(targetPixels / aspect);
      let lowW = lowH * aspect;

      // Clamp to keep look reasonable (prevents overly blocky gradients).
      const minShort = 320;
      const shortSide = Math.min(lowW, lowH);
      if (shortSide < minShort) {
        const s = minShort / shortSide;
        lowW *= s;
        lowH *= s;
      }

      w = Math.max(1, Math.floor(lowW));
      h = Math.max(1, Math.floor(lowH));

      buffer.width = w;
      buffer.height = h;
      imageData = bctx.createImageData(w, h);
      data = imageData.data;

      // Render in CSS pixels
      canvas.style.width = `${vw}px`;
      canvas.style.height = `${vh}px`;
    };

    // Debounced resize to avoid thrash.
    let resizeTimer: number | null = null;
    const onResize = () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        computeBufferSize();
      }, 120);
    };

    computeBufferSize();
    window.addEventListener("resize", onResize);

    const SIN_TABLE = new Float32Array(1024);
    const COS_TABLE = new Float32Array(1024);
    for (let i = 0; i < 1024; i++) {
      const angle = (i / 1024) * TAU;
      SIN_TABLE[i] = Math.sin(angle);
      COS_TABLE[i] = Math.cos(angle);
    }

    const fastSin = (x: number) => {
      const index =
        Math.floor(((x % TAU) / TAU) * 1024) & 1023;
      return SIN_TABLE[index];
    };

    const fastCos = (x: number) => {
      const index =
        Math.floor(((x % TAU) / TAU) * 1024) & 1023;
      return COS_TABLE[index];
    };

    const renderFrame = (now: number) => {
      if (!imageData || !data || w === 0 || h === 0) {
        rafId = window.requestAnimationFrame(renderFrame);
        return;
      }

      // Frame timing / adaptive resolution
      const dt = now - lastT;
      lastT = now;
      // Exponential moving average
      emaFrame = emaFrame * 0.9 + dt * 0.1;
      adaptAccumMs += dt;
      adaptCount += 1;

      // Adapt every ~1s
      if (adaptAccumMs >= 1000) {
        const avg = adaptAccumMs / Math.max(1, adaptCount);

        // If we're consistently slow, reduce work aggressively; if fast, increase slowly.
        if (avg > 18) {
          targetPixels = clamp(Math.floor(targetPixels * 0.78), MIN_PIXELS, MAX_PIXELS);
          computeBufferSize();
        } else if (avg < 12 && targetPixels < initialTargetPixels * 1.25) {
          targetPixels = clamp(Math.floor(targetPixels * 1.08), MIN_PIXELS, MAX_PIXELS);
          computeBufferSize();
        }

        adaptAccumMs = 0;
        adaptCount = 0;
      }

      const time = now * 0.001;

      // Precompute to reduce inner-loop overhead
      const invH = 1 / h;
      const du = 2 * invH;
      const yBase = -1;
      const xBase = -(w * invH);

      for (let y = 0; y < h; y++) {
        const u_y = yBase + 2 * y * invH;
        let u_x = xBase;
        let rowIndex = (y * w) * 4;

        for (let x = 0; x < w; x++) {
          let a = 0;
          let d = 0;

          // 4 iterations (kept for look)
          a += fastCos(0 - d + time * 0.5 - a * u_x);
          d += fastSin(0 * u_y + a);
          a += fastCos(1 - d + time * 0.5 - a * u_x);
          d += fastSin(1 * u_y + a);
          a += fastCos(2 - d + time * 0.5 - a * u_x);
          d += fastSin(2 * u_y + a);
          a += fastCos(3 - d + time * 0.5 - a * u_x);
          d += fastSin(3 * u_y + a);

          const wave = (fastSin(a) + fastCos(d)) * 0.5;
          const intensity = 0.3 + 0.4 * wave;

          // Subtle ordered dithering to reduce gradient banding after upscaling.
          // Deterministic per-pixel (no temporal noise) to avoid shimmering.
          const dither = (((x * 73856093) ^ (y * 19349663)) >>> 0) & 255;
          const ditherN = (dither / 255 - 0.5) * 0.006;

          const baseVal = 0.1 + 0.15 * fastCos(u_x + u_y + time * 0.3) + ditherN;
          const blueAccent = 0.2 * fastSin(a * 1.5 + time * 0.2);
          const purpleAccent = 0.15 * fastCos(d * 2 + time * 0.1);

          const r = clamp(baseVal + purpleAccent * 0.8, 0, 1) * intensity;
          const g = clamp(baseVal + blueAccent * 0.6, 0, 1) * intensity;
          const b = clamp(baseVal + blueAccent * 1.2 + purpleAccent * 0.4, 0, 1) * intensity;

          data[rowIndex] = r * 255;
          data[rowIndex + 1] = g * 255;
          data[rowIndex + 2] = b * 255;
          data[rowIndex + 3] = 255;

          rowIndex += 4;
          u_x += du;
        }
      }

      bctx.putImageData(imageData, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(buffer, 0, 0, buffer.width, buffer.height, 0, 0, canvas.width, canvas.height);

      rafId = window.requestAnimationFrame(renderFrame);
    };

    // Respect reduced motion: render once (no animation)
    const mql = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const prefersReduced = mql?.matches ?? false;

    const start = () => {
      lastT = performance.now();
      rafId = window.requestAnimationFrame(renderFrame);
    };

    const stop = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = null;
    };

    const renderStill = () => {
      // Render a single frame at t=now
      renderFrame(performance.now());
      stop();
    };

    if (prefersReduced) {
      renderStill();
    } else {
      start();
    }

    // Pause when hidden (battery + avoids hitch on return)
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        if (!mql?.matches) start();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    const onMotionPrefChange = () => {
      if (mql?.matches) {
        stop();
        renderStill();
      } else {
        start();
      }
    };
    mql?.addEventListener?.("change", onMotionPrefChange);

    return () => {
      stop();
      if (resizeTimer) window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      mql?.removeEventListener?.("change", onMotionPrefChange);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={
        className ??
        "pointer-events-none fixed inset-0 -z-10 h-full w-full select-none"
      }
      aria-hidden="true"
    />
  );
};

export default HeroWave;
